import { ref, onUnmounted } from 'vue';

export interface ActionFeedbackState {
  show: boolean;
  icon: string;
  text: string;
  type: 'success' | 'info' | 'error';
}

/**
 * Composable for managing action feedback overlay state
 * @param duration - How long to show the feedback (default: 1500ms)
 */
export function useActionFeedback(duration = 1500) {
  const feedback = ref<ActionFeedbackState>({
    show: false,
    icon: '',
    text: '',
    type: 'success',
  });

  let feedbackTimeout: ReturnType<typeof setTimeout> | null = null;

  function showFeedback(
    icon: string,
    text: string,
    type: 'success' | 'info' | 'error' = 'success',
  ) {
    // Clear any existing timeout
    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
    }

    // Show feedback
    feedback.value = {
      show: true,
      icon,
      text,
      type,
    };

    // Auto-hide after duration
    feedbackTimeout = setTimeout(() => {
      feedback.value.show = false;
    }, duration);
  }

  function hideFeedback() {
    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
    }
    feedback.value.show = false;
  }

  /**
   * Cleanup function to clear any pending timeouts
   * Call this on component unmount to prevent memory leaks
   */
  function cleanup() {
    if (feedbackTimeout) {
      clearTimeout(feedbackTimeout);
      feedbackTimeout = null;
    }
  }

  // Auto-cleanup when component unmounts
  onUnmounted(() => {
    cleanup();
  });

  return {
    feedback,
    showFeedback,
    hideFeedback,
    cleanup,
  };
}
